import { foldSummary } from '../components/kit'
import type { TierPreviewInput } from './api'
import { checkContractDates, checkRelationshipStart, formatDay, todayIndia } from './dateRules'

// Add a vendor in three levels of detail (release 3.3, the mentor's fourth
// point, task M6 of the 12 Oct plan).
//
// # WHY THREE LEVELS
//
// The form was one page of 18 labelled fields, 8 of them required, and most
// people adding a vendor know six things about it: its name, what kind of
// vendor it is, whose relationship it is, what it does, who to write to and
// what data it will touch. So that is Basic. Advanced adds what decides the
// tier and the contract (records, access, the four flags, legal form and
// country, the dates). Expert adds the full record (statutory numbers, the
// registered address, bank details, the profile), for the person who has the
// vendor's papers in front of them and would otherwise type them in twice.
//
// The level is a visible choice at the top of the form, remembered per person,
// and each level is the one before it plus more: nothing typed is lost by
// moving between them. A value held by a level that is not on screen is still
// saved, and the form says so in one line (rule R18 of the 12 Oct research:
// a hidden setting must show while it is hidden).
//
// # WHY THE RULES ARE HERE
//
// What is required, which level a field belongs to, what is sent, what the
// tier is asked about, and which level must open to show an error are
// decisions. In the component they could only be checked in a browser; here
// the node tests call them.

export type Level = 'basic' | 'advanced' | 'expert'

export const LEVELS: { key: Level; label: string; what: string }[] = [
  { key: 'basic', label: 'Basic', what: 'Six fields: enough to raise the request' },
  { key: 'advanced', label: 'Advanced', what: 'Adds what decides the tier, and the contract dates' },
  { key: 'expert', label: 'Expert', what: 'Adds the statutory numbers, address, bank and profile' },
]

const LEVEL_KEY = 'tprm.add-vendor.level'

export function readLevel(): Level {
  try {
    const v = localStorage.getItem(LEVEL_KEY)
    return v === 'advanced' || v === 'expert' ? v : 'basic'
  } catch {
    return 'basic'
  }
}

export function writeLevel(level: Level): void {
  try {
    localStorage.setItem(LEVEL_KEY, level)
  } catch {
    // Storage refused: the choice holds for this visit.
  }
}

export const RANK: Record<Level, number> = { basic: 0, advanced: 1, expert: 2 }

export type AddForm = {
  // Basic
  legalName: string
  vendorType: string
  ownerUserId: string
  serviceDescription: string
  contactName: string
  contactEmail: string
  dataCategories: string[]
  // Advanced
  legalForm: string
  legalFormTouched: boolean
  country: string
  businessFunction: string
  volumeBand: string
  systemAccess: string
  crossBorder: boolean
  criticalBusinessService: boolean
  productionAccess: boolean
  regulatedProcess: boolean
  contactMobile: string
  contractStartDate: string
  contractEndDate: string
  // Expert
  pan: string
  cin: string
  llpin: string
  gstin: string
  msmeCategory: string
  udyamNumber: string
  addressLine1: string
  city: string
  pinCode: string
  ifsc: string
  accountNumber: string
  vendorCategory: string
  relationshipStartDate: string
  serviceCriticality: string
  regulatoryImpact: string
  continuityDependency: string
  processingLocation: string
  dataCentreLocation: string
}

export const EMPTY_ADD: AddForm = {
  legalName: '',
  vendorType: '',
  ownerUserId: '',
  serviceDescription: '',
  contactName: '',
  contactEmail: '',
  dataCategories: [],
  legalForm: '',
  legalFormTouched: false,
  country: 'IN',
  businessFunction: '',
  volumeBand: '',
  systemAccess: '',
  crossBorder: false,
  criticalBusinessService: false,
  productionAccess: false,
  regulatedProcess: false,
  contactMobile: '',
  contractStartDate: '',
  contractEndDate: '',
  pan: '',
  cin: '',
  llpin: '',
  gstin: '',
  msmeCategory: '',
  udyamNumber: '',
  addressLine1: '',
  city: '',
  pinCode: '',
  ifsc: '',
  accountNumber: '',
  vendorCategory: '',
  relationshipStartDate: '',
  serviceCriticality: '',
  regulatoryImpact: '',
  continuityDependency: '',
  processingLocation: '',
  dataCentreLocation: '',
}

// Which level each field belongs to, with the label the summary line uses.
// Basic fields are always on screen and never summarised.
export const FIELD_LEVEL: Partial<Record<keyof AddForm, { level: Level; label: string }>> = {
  legalForm: { level: 'advanced', label: 'Legal form' },
  country: { level: 'advanced', label: 'Country' },
  businessFunction: { level: 'advanced', label: 'Business function' },
  volumeBand: { level: 'advanced', label: 'Records' },
  systemAccess: { level: 'advanced', label: 'Access' },
  crossBorder: { level: 'advanced', label: 'Data moves outside India' },
  criticalBusinessService: { level: 'advanced', label: 'Critical business service' },
  productionAccess: { level: 'advanced', label: 'Access to production' },
  regulatedProcess: { level: 'advanced', label: 'Regulated process' },
  contactMobile: { level: 'advanced', label: 'Contact mobile' },
  contractStartDate: { level: 'advanced', label: 'Contract start' },
  contractEndDate: { level: 'advanced', label: 'Contract end' },
  pan: { level: 'expert', label: 'PAN' },
  cin: { level: 'expert', label: 'CIN' },
  llpin: { level: 'expert', label: 'LLPIN' },
  gstin: { level: 'expert', label: 'GSTIN' },
  msmeCategory: { level: 'expert', label: 'MSME category' },
  udyamNumber: { level: 'expert', label: 'Udyam number' },
  addressLine1: { level: 'expert', label: 'Address' },
  city: { level: 'expert', label: 'City' },
  pinCode: { level: 'expert', label: 'PIN code' },
  ifsc: { level: 'expert', label: 'IFSC' },
  accountNumber: { level: 'expert', label: 'Account number' },
  vendorCategory: { level: 'expert', label: 'Category' },
  relationshipStartDate: { level: 'expert', label: 'Relationship start' },
  serviceCriticality: { level: 'expert', label: 'Service criticality' },
  regulatoryImpact: { level: 'expert', label: 'Regulatory impact' },
  continuityDependency: { level: 'expert', label: 'Continuity dependency' },
  processingLocation: { level: 'expert', label: 'Processing location' },
  dataCentreLocation: { level: 'expert', label: 'Data centre location' },
}

// The words for a chosen value, where the stored value is a code.
const WORDS: Partial<Record<keyof AddForm, Record<string, string>>> = {
  volumeBand: { NONE: 'none', UNDER_10K: 'under 10,000', UNDER_1M: 'under 1 million', OVER_1M: 'over 1 million' },
  systemAccess: { NONE: 'none', READ: 'read only', WRITE: 'can change data', PRIVILEGED: 'administrative' },
  legalForm: {
    PRIVATE_LIMITED: 'private limited', PUBLIC_LIMITED: 'public limited', LLP: 'LLP', PARTNERSHIP: 'partnership',
    PROPRIETORSHIP: 'proprietorship', TRUST: 'trust', SOCIETY: 'society', HUF: 'HUF', FOREIGN_ENTITY: 'foreign entity',
    GOVERNMENT_BODY: 'government body',
  },
  msmeCategory: { MICRO: 'micro', SMALL: 'small', MEDIUM: 'medium' },
  serviceCriticality: { CRITICAL: 'critical', HIGH: 'high', MEDIUM: 'medium', LOW: 'low' },
  regulatoryImpact: { HIGH: 'high', MEDIUM: 'medium', LOW: 'low' },
  continuityDependency: { HIGH: 'high', MEDIUM: 'medium', LOW: 'low' },
}

// What a field holds, as words for the summary line, or '' when it holds
// nothing a person typed or chose. India is the default country and says
// nothing; a legal form only counts once a person chose it (the one read from
// the name is shown on the field); a tick box says "yes".
function heldWords(f: AddForm, k: keyof AddForm, legalForm: string): string {
  if (k === 'country') return f.country && f.country !== 'IN' ? f.country : ''
  const v = k === 'legalForm' ? (f.legalFormTouched ? legalForm : '') : f[k]
  if (typeof v === 'boolean') return v ? 'yes' : ''
  if (Array.isArray(v)) return v.join(', ')
  const raw = String(v ?? '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatDay(raw)
  return WORDS[k]?.[raw] ?? raw
}

// The values held by fields above the level on screen: saved with the vendor,
// and said in one line so nothing is sent that the person cannot see.
export function heldAbove(f: AddForm, level: Level, legalForm: string): { label: string; value: string; level: Level }[] {
  const out: { label: string; value: string; level: Level }[] = []
  for (const [k, meta] of Object.entries(FIELD_LEVEL) as [keyof AddForm, { level: Level; label: string }][]) {
    if (RANK[meta.level] <= RANK[level]) continue
    const value = heldWords(f, k, legalForm)
    if (value) out.push({ label: meta.label, value, level: meta.level })
  }
  return out
}

// What the line under the form names: the Expert values held while Expert is
// not on screen. Advanced values are named by the Advanced fold itself while
// it is closed, so saying them twice would be a second, longer copy.
export function heldLine(f: AddForm, level: Level, legalForm: string): { label: string; value: string; level: Level }[] {
  return heldAbove(f, level, legalForm).filter((h) => h.level === 'expert')
}

// The closed Advanced fold's own summary, in Basic: what it holds. The legal
// form read from the name is said too: it is sent with the vendor and it is
// what Add and approve checks, so it must not be out of sight (review of 12
// Oct, S3).
export function advancedSummary(f: AddForm, legalForm: string): string {
  const rows = (Object.entries(FIELD_LEVEL) as [keyof AddForm, { level: Level; label: string }][])
    .filter(([, m]) => m.level === 'advanced')
    .map(([k, m]) => ({
      label: m.label,
      value:
        k === 'legalForm' && !f.legalFormTouched && legalForm
          ? (WORDS.legalForm?.[legalForm] ?? legalForm) + ' (read from the name)'
          : heldWords(f, k, legalForm),
    }))
  return foldSummary(rows)
}

// The label a field is called by in the error summary.
export function fieldLabel(k: keyof AddForm): string {
  return FIELD_LEVEL[k]?.label ?? BASIC_LABELS[k] ?? String(k)
}

const BASIC_LABELS: Partial<Record<keyof AddForm, string>> = {
  legalName: 'Vendor name',
  vendorType: 'Vendor type',
  ownerUserId: 'Business owner',
  serviceDescription: 'Service provided',
  contactName: 'Contact name',
  contactEmail: 'Contact e-mail',
  dataCategories: 'Data they will touch',
}

// ------------------------------------------------------------ the checks

// An Indian mobile as people write it at home. The form stores it with +91 in
// front on save, the only form the contacts route accepts.
export const INDIAN_MOBILE = /^[6-9][0-9]{9}$/

export const REQUIRED: (keyof AddForm)[] = ['legalName', 'vendorType', 'ownerUserId', 'contactName', 'contactEmail']

// What is wrong, field by field, in the words shown under the field. The
// required set is the same at every level: Advanced and Expert add fields a
// person may fill, never fields they must.
export function checkAdd(f: AddForm, today: string = todayIndia()): Partial<Record<keyof AddForm, string>> {
  const e: Partial<Record<keyof AddForm, string>> = {}
  if (!f.legalName.trim()) e.legalName = 'Enter the vendor name'
  if (!f.vendorType) e.vendorType = 'Choose the vendor type'
  if (!f.ownerUserId) e.ownerUserId = 'Choose the business owner'
  if (!f.contactName.trim()) e.contactName = 'Enter the contact name'
  const email = f.contactEmail.trim()
  if (!email) e.contactEmail = 'Enter the contact e-mail'
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) e.contactEmail = 'Enter an e-mail address like name@vendor.com'
  if (f.country !== 'IN' && !/^[A-Z]{2}$/.test(f.country.trim().toUpperCase())) e.country = 'Enter the two-letter country code, for example US'
  if (f.country === 'IN') {
    if (f.contactMobile && !INDIAN_MOBILE.test(f.contactMobile)) e.contactMobile = 'A mobile number is 10 digits and starts with 6, 7, 8 or 9'
  } else if (f.contactMobile.trim() && !/^\+[1-9][0-9]{7,14}$/.test(f.contactMobile.trim())) {
    e.contactMobile = 'Use the international form, for example +14155550100'
  }
  const dates = checkContractDates(f.contractStartDate, f.contractEndDate)
  if (dates) e.contractEndDate = dates
  const rel = checkRelationshipStart(f.relationshipStartDate, today)
  if (rel) e.relationshipStartDate = rel
  return e
}

// # ADD AND APPROVE (release 3.3, M5 with M6)
//
// A vendor added here is a request: nothing can be sent to it until a TPRM
// lead approves the request (M5). A TPRM lead adding a vendor is that
// approver, so the form offers them "Add and approve request" beside "Add
// vendor", and the server records the approval as theirs (POST /v1/vendors
// with approveRequest; domain.CheckRequestDecision decides). Two cases where it
// is not offered or not possible, said before the click rather than after:
//   - the reader cannot approve requests, or the organisation asks a second
//     person to approve a vendor request (one person cannot add and approve);
//     an unread setting counts as "ask a second person", so nothing is offered
//     that the server might refuse;
//   - the legal form is not known yet: the server refuses to approve a request
//     without it (LEGAL_FORM_MISSING), so the form asks for it on the field.
export function approveOffered(mayApprove: boolean, policy: { secondPersonApproves: boolean } | null | undefined): boolean {
  return mayApprove && !!policy && !policy.secondPersonApproves
}

export const APPROVE_NEEDS_LEGAL_FORM = 'Choose the legal form to approve the request now, or add the vendor without approving it'

// What else must be true to approve the request in the same step: the legal
// form, chosen or read from the name. Added to checkAdd's errors.
export function checkApproveNow(legalForm: string): Partial<Record<keyof AddForm, string>> {
  return legalForm.trim() ? {} : { legalForm: APPROVE_NEEDS_LEGAL_FORM }
}

// The level that has to be on screen for every error to be seen: an error on
// a field the person cannot see is an error they cannot fix.
export function levelForErrors(errors: Partial<Record<keyof AddForm, string>>, level: Level): Level {
  let need = level
  for (const k of Object.keys(errors) as (keyof AddForm)[]) {
    const m = FIELD_LEVEL[k]
    if (m && RANK[m.level] > RANK[need]) need = m.level
  }
  return need
}

// ---------------------------------------------------------- what is sent

// The contact mobile as the API wants it: +91 and the ten digits for a vendor
// in India, the international form as typed for anywhere else.
export function mobileForApi(country: string, typed: string): string {
  const v = typed.trim()
  if (v === '') return ''
  return country === 'IN' ? '+91' + v : v
}

// The create request. Always BASIC: whatever statutory number is left blank
// becomes a gap on the vendor (to fill before it is approved) instead of a
// refusal, and whatever is typed is checked as strictly as the full form
// checks it. Every field is sent whatever the level on screen, because a value
// held above the level is shown in the summary line and is the person's.
export function createBody(f: AddForm, legalForm: string, approve = false): Record<string, unknown> {
  const t = (s: string) => s.trim()
  const country = t(f.country).toUpperCase() || 'IN'
  return {
    ...(approve ? { approveRequest: true } : {}),
    onboardingMode: 'BASIC',
    legalName: t(f.legalName),
    vendorType: f.vendorType,
    businessOwnerUserId: f.ownerUserId,
    serviceDescription: t(f.serviceDescription),
    dataCategories: f.dataCategories,
    constitution: legalForm,
    country,
    businessFunction: t(f.businessFunction),
    volumeBand: f.volumeBand,
    systemAccess: f.systemAccess,
    crossBorder: f.crossBorder,
    criticalBusinessService: f.criticalBusinessService,
    productionAccess: f.productionAccess,
    regulatedProcess: f.regulatedProcess,
    contractStartDate: f.contractStartDate,
    contractEndDate: f.contractEndDate,
    pan: t(f.pan).toUpperCase(),
    cin: t(f.cin).toUpperCase(),
    llpin: t(f.llpin).toUpperCase(),
    gstins: t(f.gstin) ? [t(f.gstin).toUpperCase()] : [],
    msmeCategory: f.msmeCategory,
    udyamNumber: t(f.udyamNumber).toUpperCase(),
    addressLine1: t(f.addressLine1),
    city: t(f.city),
    pinCode: t(f.pinCode),
    ifsc: t(f.ifsc).toUpperCase(),
    accountNumber: t(f.accountNumber),
    vendorCategory: t(f.vendorCategory),
    relationshipStartDate: f.relationshipStartDate,
    serviceCriticality: f.serviceCriticality,
    regulatoryImpact: f.regulatoryImpact,
    continuityDependency: f.continuityDependency,
    processingLocation: t(f.processingLocation),
    dataCentreLocation: t(f.dataCentreLocation),
  }
}

// What the tier rules are asked about: exactly the create's own values, so the
// tier the panel shows is the tier the vendor gets.
export function tierInputs(f: AddForm): TierPreviewInput {
  return {
    dataCategories: [...f.dataCategories].sort(),
    volumeBand: f.volumeBand,
    systemAccess: f.systemAccess,
    regulatedProcess: f.regulatedProcess,
    crossBorder: f.crossBorder,
    criticalBusinessService: f.criticalBusinessService,
    productionAccess: f.productionAccess,
    regulatoryImpact: f.regulatoryImpact,
    continuityDependency: f.continuityDependency,
  }
}

// Whether anything the tier rules read has been answered. With nothing
// answered the rules still give a tier (the vendor gets it on save), and the
// panel says it rests on no answers yet.
export function tierAnswered(f: AddForm): boolean {
  const i = tierInputs(f)
  return (
    i.dataCategories.length > 0 ||
    i.volumeBand !== '' ||
    i.systemAccess !== '' ||
    i.regulatedProcess ||
    i.crossBorder ||
    i.criticalBusinessService ||
    i.productionAccess ||
    (i.regulatoryImpact ?? '') !== '' ||
    (i.continuityDependency ?? '') !== ''
  )
}

// The data row: four kinds and "None". None is pressed when nothing is, and
// pressing it clears the others; pressing a kind adds or removes it.
export const DATA_KINDS: [string, string][] = [
  ['PERSONAL', 'Personal'],
  ['SENSITIVE_PERSONAL', 'Sensitive'],
  ['FINANCIAL', 'Financial'],
  ['HEALTH', 'Health'],
]

export function toggleData(current: string[], value: string): string[] {
  if (value === 'NONE') return []
  return current.includes(value) ? current.filter((x) => x !== value) : [...current, value]
}

// A refusal from the API names its field in its own words; this is which box
// that is.
export const ISSUE_FIELD: Record<string, keyof AddForm> = {
  legal_name: 'legalName',
  legalName: 'legalName',
  constitution: 'legalForm',
  vendorType: 'vendorType',
  businessOwnerUserId: 'ownerUserId',
  businessFunction: 'businessFunction',
  country: 'country',
  email: 'contactEmail',
  fullName: 'contactName',
  mobileE164: 'contactMobile',
  contractStartDate: 'contractStartDate',
  contractEndDate: 'contractEndDate',
  volumeBand: 'volumeBand',
  systemAccess: 'systemAccess',
  pan: 'pan',
  cin: 'cin',
  llpin: 'llpin',
  gstin: 'gstin',
  gstins: 'gstin',
  udyam_number: 'udyamNumber',
  udyamNumber: 'udyamNumber',
  msmeCategory: 'msmeCategory',
  pin_code: 'pinCode',
  pinCode: 'pinCode',
  ifsc: 'ifsc',
  account_number: 'accountNumber',
  accountNumber: 'accountNumber',
  relationshipStartDate: 'relationshipStartDate',
  serviceCriticality: 'serviceCriticality',
  regulatoryImpact: 'regulatoryImpact',
  continuityDependency: 'continuityDependency',
}

// Which statutory number the legal form asks for: a company has a CIN, an LLP
// an LLPIN, and nothing else has either.
export function registrationField(legalForm: string): 'cin' | 'llpin' | null {
  if (legalForm === 'PRIVATE_LIMITED' || legalForm === 'PUBLIC_LIMITED') return 'cin'
  if (legalForm === 'LLP') return 'llpin'
  return null
}
